import { useEffect, useState, useRef } from 'react';
import { hysteriFetch } from 'hysterics';
import { useRequestData } from 'wsm-common-data';
import { isBrowser } from 'ws-scripts/modules/environment';
import {
	emptyInventoryObj,
	options,
	complementaryInventoryObj
} from '../global-constants';
import { getMessageObject, alertMessageIds } from '../data/alertMessage';

const NR_PREFIX = 'MYCARS_SELECT';
const ENDPOINT_FETCH_ERROR = `${NR_PREFIX}/CLIENT/VEHICLES_ENDPOINT_FETCH_ERROR`;
const ENDPOINT_FETCH_SUCCESS = `${NR_PREFIX}/CLIENT/VEHICLES_ENDPOINT_FETCH_SUCCESS`;

const vehicleDataService = hysteriFetch('vehicleData', {
	timeout: {
		timeout: 2000
	}
});

const newrelicAction = (action, facets) => {
	if (window && window.newrelic && typeof window.newrelic === 'object') {
		window.newrelic.addPageAction(action, facets);
	}
};

const publishEvent = (topic, ...params) => {
	if (isBrowser && window?.DDC?.pubsub) {
		window.DDC.pubsub.publish(topic, ...params);
	}
};

const updateVehicles = (myCarsData, inventory, prop) => {
	const inventoryUUIDs = inventory.map((v) => v.uuid);
	const vehiclesToFilter = myCarsData?.vehicles ? myCarsData?.vehicles : [];
	const updatedVehicles = vehiclesToFilter.map((vehicle) => {
		if (
			!inventoryUUIDs.includes(vehicle.uuid) &&
			vehicle[prop] !== undefined
		) {
			// eslint-disable-next-line no-param-reassign
			delete vehicle[prop];
		}
		return vehicle;
	});

	// the viewed property remaining does not count to keep the vehicle in the user's profile
	const finalVehicles = updatedVehicles.filter((vehicle) => {
		return (
			vehicle[options.SAVED] !== undefined ||
			vehicle[options.TRACKED_PRICE] !== undefined
		);
	});
	return finalVehicles;
};

export const sortRecentByProperty = (array, property) => {
	if (!Array.isArray(array)) return [];
	return array.sort((a, b) => {
		const aDate = new Date(a[property] * 1000);
		const bDate = new Date(b[property] * 1000);
		return bDate - aDate;
	});
};

const filterByProperty = (array, property) => {
	if (!Array.isArray(array)) return [];
	return array.filter((item) => item[property] !== '' && item[property]);
};

export const useFetchVehicleData = (
	hookProps,
	facetValue,
	updateState,
	myCarsData
) => {
	const { deviceType, locale } = useRequestData();
	const vehiclesList = filterByProperty(myCarsData?.vehicles, facetValue);
	const vehiclesListUUIDs = vehiclesList.map((v) => v.uuid);
	const [response, setResponse] = useState({});
	const facetValueReference = useRef('');

	const fetchVehicleData = async () => {
		if (vehiclesListUUIDs.length > 0) {
			const { siteId, pageAlias, pageId } = hookProps;
			const requestUrl = '/api/widget/ws-mycars-select/vehicles';

			const requestBody = {
				siteId,
				locale,
				device: deviceType?.toLowerCase(),
				pageAlias,
				pageId,
				inventoryParameters: { uuids: vehiclesListUUIDs }
			};

			const requestBodyStringified = JSON.stringify(requestBody);
			const startTime = Date.now();
			let inventoryResponse;
			let inventory = [];
			let sortedInventory = {};
			let sortedVehicleData = [];
			let alertMessage = {};
			let lessInventoryAmount = 0;

			try {
				publishEvent('ws-inv-data/inventory-request');
				inventoryResponse = await vehicleDataService(requestUrl, {
					method: 'POST',
					headers: {
						'content-type': 'application/json;charset=UTF-8'
					},
					body: requestBodyStringified
				});
				const sameData = !!inventoryResponse?.sameData;
				if (inventoryResponse?.wisResponse) {
					inventory = inventoryResponse.wisResponse;
					lessInventoryAmount =
						vehiclesListUUIDs.length - inventory.length;

					if (inventory.length > 0) {
						const withTimeStamp = inventory
							.map((filteredVehicle) => {
								const vehicle = vehiclesList.find(
									({ uuid: vehicleUuid }) => {
										return (
											vehicleUuid ===
											filteredVehicle?.uuid
										);
									}
								);

								if (!vehicle) {
									return undefined;
								}
								return {
									...vehicle,
									...filteredVehicle
								};
							})
							.filter(Boolean);
						sortedVehicleData = sortRecentByProperty(
							withTimeStamp,
							facetValue
						);

						sortedInventory = {
							inventory: sortedVehicleData,
							...complementaryInventoryObj
						};
					} else {
						// When WIS returns no inventory
						alertMessage = {
							...getMessageObject(
								facetValue,
								alertMessageIds.NO_INVENTORY,
								vehiclesListUUIDs.length !== 1,
								vehiclesListUUIDs.length
							)
						};
					}

					if (!sameData && inventory.length > 0) {
						// When WIS returns less vehicles data than we expect
						alertMessage = {
							...getMessageObject(
								facetValue,
								alertMessageIds.LESS_INVENTORY,
								lessInventoryAmount !== 1,
								// no message for less inventory then expected when is viewed
								facetValue !== options.RECENTLY_VIEWED
									? lessInventoryAmount
									: undefined
							)
						};
					}

					if (
						inventory.length === 0 ||
						(!sameData && inventory.length > 0)
					) {
						// update vehicles user's profile data
						const updatedVehicles = updateVehicles(
							myCarsData,
							inventory || [],
							facetValue
						);
						updateState({
							...myCarsData,
							vehicles: updatedVehicles
						});
					}
					const inventoryData =
						sortedVehicleData.length > 0
							? sortedInventory
							: emptyInventoryObj;

					if (window.DDC.InvData) {
						window.DDC.InvData.inventory = inventoryData;
						window.DDC.InvData.alertMessage = alertMessage;
						publishEvent('ws-inv-data/inventory');
					}
					setResponse(inventoryData);
				} else {
					publishEvent('ws-inv-data/request-failed');
				}
			} catch (e) {
				publishEvent('ws-inv-data/request-failed');
				const { message: error } = e;
				newrelicAction(`${ENDPOINT_FETCH_ERROR}`, {
					requestUrl,
					error
				});
				throw new Error(`${ENDPOINT_FETCH_ERROR}: ${e.message}`);
			}
			const requestDuration = Date.now() - startTime;
			newrelicAction(`${ENDPOINT_FETCH_SUCCESS}`, {
				requestUrl,
				responseStatus: 200,
				requestDuration,
				totalVehicles: inventory.length ?? 0
			});
		} else if (window.DDC.InvData) {
			window.DDC.InvData.inventory = emptyInventoryObj;
			// msg no vehicles are present in the user's profile data
			window.DDC.InvData.alertMessage = {
				...getMessageObject(
					facetValue,
					alertMessageIds.EMPTY_PROFILE_DATA
				)
			};
			publishEvent('ws-inv-data/inventory');
		}
	};

	useEffect(() => {
		if (facetValueReference.current !== facetValue) {
			fetchVehicleData();
			facetValueReference.current = facetValue;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [facetValue]);

	return { response };
};
